import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';

const MENSAGEM_FALHA_DASHBOARD =
  'Não foi possível carregar o dashboard de estoque. Verifique a conexão e tente de novo; se continuar, avise a sede.';
const MENSAGEM_FALHA_PRODUTOS =
  'Não foi possível carregar os produtos desta faixa. Tente de novo em instantes.';

const CORES_DA_FAIXA = { zerado: '#f5222d', baixo: '#fa8c16', normal: '#1890ff', alto: '#52c41a' };

// Hook personalizado para responsividade
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isMobile;
};

const EstoqueDashboard = () => {
  const isMobile = useIsMobile();
  const [loading, setLoading] = useState(true);
  const [resumo, setResumo] = useState({});
  const [produtos, setProdutos] = useState([]);
  const [movimentacoes, setMovimentacoes] = useState([]);
  const [alertas, setAlertas] = useState([]);
  const [erro, setErro] = useState('');
  const [erroProdutos, setErroProdutos] = useState('');
  
  const [filtroCategoria, setFiltroCategoria] = useState('baixo');

  useEffect(() => {
    carregarDados();
  }, []);

  useEffect(() => {
    carregarProdutosPorCategoria();
  }, [filtroCategoria]);

  const carregarDados = async () => {
    try {
      setLoading(true);
      setErro('');
      
      const [
        resumoRes,
        movimentacoesRes,
        alertasRes
      ] = await Promise.all([
        api.get('/estoque-dashboard/resumo'),
        api.get('/estoque-dashboard/movimentacoes?limit=10'),
        api.get('/estoque-dashboard/alertas')
      ]);

      setResumo(resumoRes.data.data);
      setMovimentacoes(movimentacoesRes.data.data);
      setAlertas(alertasRes.data.data);

    } catch (error) {
      // Sem dados não há o que mostrar: zeros nos cards pareceriam estoque vazio.
      setErro(MENSAGEM_FALHA_DASHBOARD);
    } finally {
      setLoading(false);
    }
  };

  const carregarProdutosPorCategoria = async () => {
    try {
      setErroProdutos('');
      const response = await api.get('/estoque-dashboard/produtos', { params: { categoria: filtroCategoria } });
      setProdutos(response.data.data);
    } catch (error) {
      setProdutos([]);
      setErroProdutos(MENSAGEM_FALHA_PRODUTOS);
    }
  };

  const formatarMoeda = (valor) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(valor);
  };

  const getStatusBadge = (produto) => {
    return (
      <span style={{ 
        backgroundColor: CORES_DA_FAIXA[produto.categoriaEstoque] || '#666',
        color: 'white', 
        padding: isMobile ? '1px 6px' : '2px 8px', 
        borderRadius: '4px',
        fontSize: isMobile ? '10px' : '12px',
        fontWeight: '500'
      }}>
        {produto.categoriaRotulo}
      </span>
    );
  };

  const faixaBaixo = (resumo.faixas || []).find(f => f.categoria === 'baixo');
  const saldosNegativos = resumo.saldosNegativos || [];
  const alertasVisiveis = alertas.slice(0, isMobile ? 4 : 8);

  const getAlertIcon = (tipo) => {
    const icons = {
      critico: '🚨',
      aviso: '⚠️',
      info: 'ℹ️'
    };
    return icons[tipo] || 'ℹ️';
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <div>Carregando dashboard...</div>
      </div>
    );
  }

  if (erro) {
    return (
      <div style={{ padding: isMobile ? '12px' : '24px', boxSizing: 'border-box', maxWidth: '100%' }}>
        <h1 style={{ color: '#809671', fontSize: isMobile ? '24px' : '28px', fontWeight: 'bold', margin: '0 0 16px 0' }}>
          Dashboard de Estoque
        </h1>
        <div role="alert" style={{
          padding: '16px',
          borderRadius: '8px',
          backgroundColor: '#fff2f0',
          border: '1px solid #ffccc7',
          color: '#a8071a',
          marginBottom: '12px',
          overflowWrap: 'anywhere'
        }}>
          {erro}
        </div>
        <button
          type="button"
          onClick={carregarDados}
          style={{ padding: '10px 16px', borderRadius: '4px', border: '1px solid #d9d9d9', background: 'white', cursor: 'pointer' }}
        >
          Tentar de novo
        </button>
      </div>
    );
  }

  return (
    <div style={{ 
      padding: isMobile ? '12px' : '24px',
      width: '100%',
      maxWidth: '100%',
      boxSizing: 'border-box',
      overflowX: 'hidden'
    }}>
      <div style={{ marginBottom: isMobile ? 16 : 24 }}>
        <h1 style={{ 
          color: '#809671', 
          fontSize: isMobile ? '24px' : '28px', 
          fontWeight: 'bold',
          margin: '0 0 8px 0'
        }}>
          📊 Dashboard de Estoque
        </h1>
        <p style={{ 
          color: '#666', 
          fontSize: isMobile ? '14px' : '16px',
          margin: 0
        }}>
          Acompanhamento em tempo real do estoque de produtos
        </p>
      </div>

      {/* Cards de Resumo */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: isMobile 
          ? 'repeat(2, 1fr)' 
          : 'repeat(auto-fit, minmax(250px, 1fr))', 
        gap: isMobile ? '8px' : '16px', 
        marginBottom: isMobile ? '16px' : '24px' 
      }}>
        <div style={{ 
          background: 'white', 
          padding: isMobile ? '12px' : '20px', 
          borderRadius: '8px', 
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)', 
          border: '1px solid #f0f0f0' 
        }}>
          <div style={{ 
            fontSize: isMobile ? '12px' : '14px', 
            color: '#666', 
            marginBottom: isMobile ? '4px' : '8px' 
          }}>
            Produtos com Controle
          </div>
          <div style={{ 
            fontSize: isMobile ? '18px' : '24px', 
            fontWeight: 'bold', 
            color: '#809671' 
          }}>
            🛍️ {resumo.produtosComControle || 0}
          </div>
        </div>
        
        <div style={{ 
          background: 'white', 
          padding: isMobile ? '12px' : '20px', 
          borderRadius: '8px', 
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)', 
          border: '1px solid #f0f0f0' 
        }}>
          <div style={{ 
            fontSize: isMobile ? '12px' : '14px', 
            color: '#666', 
            marginBottom: isMobile ? '4px' : '8px' 
          }}>
            Produtos em Falta
          </div>
          <div style={{ 
            fontSize: isMobile ? '18px' : '24px', 
            fontWeight: 'bold', 
            color: '#f5222d' 
          }}>
            🚨 {resumo.produtosZerados || 0}
          </div>
        </div>
        
        <div style={{ 
          background: 'white', 
          padding: isMobile ? '12px' : '20px', 
          borderRadius: '8px', 
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)', 
          border: '1px solid #f0f0f0' 
        }}>
          <div style={{ 
            fontSize: isMobile ? '12px' : '14px', 
            color: '#666', 
            marginBottom: isMobile ? '4px' : '8px' 
          }}>
            Estoque baixo{faixaBaixo ? ` (${faixaBaixo.minimo} a ${faixaBaixo.maximo} disp.)` : ''}
          </div>
          <div style={{ 
            fontSize: isMobile ? '18px' : '24px', 
            fontWeight: 'bold', 
            color: '#fa8c16' 
          }}>
            ⚠️ {resumo.produtosBaixoEstoque || 0}
          </div>
        </div>
        
        <div style={{ 
          background: 'white', 
          padding: isMobile ? '12px' : '20px', 
          borderRadius: '8px', 
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)', 
          border: '1px solid #f0f0f0',
          gridColumn: isMobile ? 'span 2' : 'auto'
        }}>
          <div style={{ 
            fontSize: isMobile ? '12px' : '14px', 
            color: '#666', 
            marginBottom: isMobile ? '4px' : '8px' 
          }}>
            Valor do estoque (a preço de venda)
          </div>
          <div style={{ 
            fontSize: isMobile ? '16px' : '20px', 
            fontWeight: 'bold', 
            color: '#52c41a' 
          }}>
            💰 {formatarMoeda(resumo.valorTotalEstoque || 0)}
          </div>
        </div>
      </div>

      {saldosNegativos.length > 0 && (
        <div style={{
          marginBottom: isMobile ? '16px' : '24px',
          padding: isMobile ? '12px' : '16px',
          borderRadius: '8px',
          backgroundColor: '#fffbe6',
          border: '1px solid #ffe58f',
          overflowWrap: 'anywhere'
        }}>
          <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>Saldos negativos a corrigir</div>
          <div style={{ fontSize: isMobile ? '12px' : '13px', color: '#666', marginBottom: '6px' }}>
            Saiu mais do que entrou nestes produtos. Eles ficam fora do valor do estoque até que a entrada que falta seja lançada.
          </div>
          <ul style={{ margin: 0, paddingLeft: '18px', fontSize: isMobile ? '12px' : '14px' }}>
            {saldosNegativos.map(p => (
              <li key={p.idProduto}>{p.produtoNome} (cód. {p.idProduto}): {p.estoqueFisico} no físico</li>
            ))}
          </ul>
        </div>
      )}

      {/* Movimentações do Mês */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: '1fr', 
        gap: isMobile ? '12px' : '16px', 
        marginBottom: isMobile ? '16px' : '24px' 
      }}>
        <div style={{ 
          background: 'white', 
          padding: isMobile ? '16px' : '20px', 
          borderRadius: '8px', 
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)', 
          border: '1px solid #f0f0f0' 
        }}>
          <h3 style={{ 
            margin: '0 0 12px 0', 
            color: '#333',
            fontSize: isMobile ? '16px' : '18px'
          }}>
            Movimentações do Mês
          </h3>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            flexDirection: isMobile ? 'row' : 'row'
          }}>
            <div style={{ textAlign: 'center', flex: 1 }}>
              <div style={{ 
                fontSize: isMobile ? '20px' : '24px', 
                fontWeight: 'bold', 
                color: '#52c41a' 
              }}>
                ↗️ {resumo.movimentacoesMes?.entradas || 0}
              </div>
              <div style={{ 
                fontSize: isMobile ? '11px' : '12px', 
                color: '#666' 
              }}>
                Entradas ({resumo.movimentacoesMes?.numeroEntradas || 0} mov.)
              </div>
            </div>
            <div style={{ 
              width: '1px', 
              backgroundColor: '#f0f0f0', 
              margin: isMobile ? '0 8px' : '0 16px' 
            }}></div>
            <div style={{ textAlign: 'center', flex: 1 }}>
              <div style={{ 
                fontSize: isMobile ? '20px' : '24px', 
                fontWeight: 'bold', 
                color: '#f5222d' 
              }}>
                ↘️ {resumo.movimentacoesMes?.saidas || 0}
              </div>
              <div style={{ 
                fontSize: isMobile ? '11px' : '12px', 
                color: '#666' 
              }}>
                Saídas ({resumo.movimentacoesMes?.numeroSaidas || 0} mov.)
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Alertas */}
      {alertas.length > 0 && (
        <div style={{ marginBottom: isMobile ? '16px' : '24px' }}>
          <div style={{ 
            background: 'white', 
            padding: isMobile ? '16px' : '20px', 
            borderRadius: '8px', 
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)', 
            border: '1px solid #f0f0f0' 
          }}>
            <h3 style={{ 
              margin: '0 0 12px 0', 
              color: '#333',
              fontSize: isMobile ? '16px' : '18px'
            }}>
              Alertas do Estoque
            </h3>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: isMobile 
                ? '1fr' 
                : 'repeat(auto-fit, minmax(250px, 1fr))', 
              gap: isMobile ? '6px' : '8px' 
            }}>
              {alertasVisiveis.map((alerta, index) => (
                <div 
                  key={index}
                  style={{ 
                    padding: isMobile ? '10px' : '12px', 
                    borderRadius: '6px', 
                    backgroundColor: alerta.tipo === 'critico' ? '#fff2f0' : alerta.tipo === 'aviso' ? '#fff7e6' : '#e6f7ff',
                    border: `1px solid ${alerta.tipo === 'critico' ? '#ffccc7' : alerta.tipo === 'aviso' ? '#ffd591' : '#91d5ff'}`
                  }}
                >
                  <div style={{ 
                    fontSize: isMobile ? '13px' : '14px', 
                    fontWeight: 'bold', 
                    marginBottom: '4px' 
                  }}>
                    {getAlertIcon(alerta.tipo)} {alerta.titulo}
                  </div>
                  <div style={{ 
                    fontSize: isMobile ? '11px' : '12px', 
                    color: '#666' 
                  }}>
                    {alerta.descricao}
                  </div>
                </div>
              ))}
            </div>
            {alertas.length > alertasVisiveis.length && (
              <div style={{ marginTop: '8px', fontSize: isMobile ? '12px' : '13px', color: '#666' }}>
                Mostrando {alertasVisiveis.length} de {alertas.length} alertas.{' '}
                <Link to="/estoque/relatorio">Ver todos no Relatório de Estoque</Link>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tabelas */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(400px, 1fr))', 
        gap: isMobile ? '12px' : '16px',
        width: '100%',
        maxWidth: '100%',
        overflow: 'hidden'
      }}>
        <div style={{ 
          background: 'white', 
          padding: isMobile ? '16px' : '20px', 
          borderRadius: '8px', 
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)', 
          border: '1px solid #f0f0f0',
          width: '100%',
          boxSizing: 'border-box',
          overflow: 'hidden'
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginBottom: '12px',
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? '8px' : '0'
          }}>
            <h3 style={{ 
              margin: 0, 
              color: '#333',
              fontSize: isMobile ? '16px' : '18px'
            }}>
              Produtos por Categoria
            </h3>
            <select 
              value={filtroCategoria} 
              onChange={(e) => setFiltroCategoria(e.target.value)}
              style={{ 
                padding: '6px 10px', 
                borderRadius: '4px', 
                border: '1px solid #d9d9d9',
                fontSize: isMobile ? '14px' : '16px',
                width: isMobile ? '100%' : 'auto'
              }}
            >
              <option value="zerado">Zerados</option>
              <option value="baixo">Baixo</option>
              <option value="normal">Normal</option>
              <option value="alto">Alto</option>
            </select>
          </div>
          <div style={{ 
            maxHeight: isMobile ? '250px' : '300px', 
            overflowY: 'auto',
            overflowX: 'auto',
            width: '100%',
            border: '1px solid #f0f0f0',
            borderRadius: '4px'
          }}>
            {produtos.length > 0 ? (
              <table style={{ 
                width: '100%', 
                borderCollapse: 'collapse',
                minWidth: isMobile ? '350px' : '100%',
                tableLayout: isMobile ? 'fixed' : 'auto'
              }}>
                <thead>
                  <tr style={{ backgroundColor: '#fafafa' }}>
                    <th style={{ 
                      padding: isMobile ? '6px 4px' : '8px', 
                      textAlign: 'left', 
                      borderBottom: '1px solid #f0f0f0',
                      fontSize: isMobile ? '12px' : '14px',
                      width: isMobile ? '40%' : 'auto'
                    }}>
                      Produto
                    </th>
                    <th style={{ 
                      padding: isMobile ? '6px 4px' : '8px', 
                      textAlign: 'center', 
                      borderBottom: '1px solid #f0f0f0',
                      fontSize: isMobile ? '12px' : '14px',
                      width: isMobile ? '35%' : 'auto'
                    }}>
                      Estoque
                    </th>
                    <th style={{ 
                      padding: isMobile ? '6px 4px' : '8px', 
                      textAlign: 'right', 
                      borderBottom: '1px solid #f0f0f0',
                      fontSize: isMobile ? '12px' : '14px',
                      width: isMobile ? '25%' : 'auto'
                    }}>
                      Valor
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {produtos.map((produto) => (
                    <tr key={produto.idProduto}>
                      <td style={{ 
                        padding: isMobile ? '6px 4px' : '8px', 
                        borderBottom: '1px solid #f0f0f0',
                        maxWidth: isMobile ? '0' : 'none',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: isMobile ? 'nowrap' : 'normal'
                      }}>
                        <div style={{ 
                          fontSize: isMobile ? '12px' : '14px', 
                          fontWeight: '500',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {produto.produtoNome}
                        </div>
                        <div style={{ 
                          fontSize: isMobile ? '10px' : '12px', 
                          color: '#666',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {produto.tipoProdutoNome}
                        </div>
                      </td>
                      <td style={{ 
                        padding: isMobile ? '6px 4px' : '8px', 
                        textAlign: 'center', 
                        borderBottom: '1px solid #f0f0f0' 
                      }}>
                        <div style={{ 
                          fontSize: isMobile ? '13px' : '15px',
                          fontWeight: 'bold',
                          color: produto.estoqueDisponivel <= 0 ? '#f5222d' : '#333',
                          marginBottom: '2px'
                        }}>
                          {produto.estoqueDisponivel} {!isMobile && 'disp.'}
                        </div>
                        {produto.quantidadeReservada > 0 && (
                          <div style={{ 
                            fontSize: isMobile ? '10px' : '11px',
                            color: '#666',
                            marginBottom: '2px'
                          }}>
                            Físico: {produto.estoqueFisico}
                          </div>
                        )}
                        {produto.quantidadeReservada > 0 && (
                          <div style={{ 
                            fontSize: isMobile ? '10px' : '11px',
                            color: '#fa8c16',
                            fontWeight: '500',
                            marginBottom: '2px'
                          }}>
                            🔒 {produto.quantidadeReservada} reserv.
                          </div>
                        )}
                        {getStatusBadge(produto)}
                      </td>
                      <td style={{ 
                        padding: isMobile ? '6px 4px' : '8px', 
                        textAlign: 'right', 
                        borderBottom: '1px solid #f0f0f0' 
                      }}>
                        <div style={{ fontSize: isMobile ? '11px' : '14px' }}>
                          {formatarMoeda(produto.valor || 0)}
                        </div>
                        <div style={{ 
                          fontSize: isMobile ? '10px' : '12px', 
                          color: '#666' 
                        }}>
                          {formatarMoeda(produto.valorEstoque || 0)}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div style={{ 
                textAlign: 'center', 
                padding: '20px', 
                color: '#999',
                fontSize: isMobile ? '14px' : '16px'
              }}>
                {erroProdutos || 'Nenhum produto encontrado nesta categoria'}
              </div>
            )}
          </div>
        </div>
        
        <div style={{ 
          background: 'white', 
          padding: isMobile ? '16px' : '20px', 
          borderRadius: '8px', 
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)', 
          border: '1px solid #f0f0f0' 
        }}>
          <h3 style={{ 
            margin: '0 0 12px 0', 
            color: '#333',
            fontSize: isMobile ? '16px' : '18px'
          }}>
            Movimentações Recentes
          </h3>
          <div style={{ 
            maxHeight: isMobile ? '250px' : '300px', 
            overflowY: 'auto' 
          }}>
            {movimentacoes.length > 0 ? (
              <div>
                {movimentacoes.map((mov, index) => (
                  <div 
                    key={`${mov.tipo}-${mov.id}`}
                    style={{ 
                      padding: isMobile ? '8px 0' : '12px 0', 
                      borderBottom: index < movimentacoes.length - 1 ? '1px solid #f0f0f0' : 'none'
                    }}
                  >
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'flex-start' 
                    }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ 
                          fontSize: isMobile ? '12px' : '14px', 
                          fontWeight: '500', 
                          marginBottom: '4px' 
                        }}>
                          <span style={{ 
                            backgroundColor: mov.tipo === 'entrada' ? '#f6ffed' : '#fff2f0',
                            color: mov.tipo === 'entrada' ? '#52c41a' : '#f5222d',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            fontSize: isMobile ? '10px' : '12px',
                            marginRight: '8px'
                          }}>
                            {mov.tipo === 'entrada' ? '↗️ Entrada' : '↘️ Saída'}
                          </span>
                          <span style={{ fontSize: isMobile ? '11px' : '14px' }}>
                            {mov.produtoNome?.length > 20 && isMobile 
                              ? `${mov.produtoNome.substring(0, 20)}...` 
                              : mov.produtoNome
                            }
                          </span>
                        </div>
                        <div style={{ 
                          fontSize: isMobile ? '10px' : '12px', 
                          color: '#666' 
                        }}>
                          {(() => {
                            if (!mov.dataMovimento) return '-';
                            // Criar data ajustada para evitar problemas de fuso horário
                            const date = new Date(mov.dataMovimento);
                            const adjustedDate = new Date(date.getTime() + date.getTimezoneOffset() * 60000);
                            return adjustedDate.toLocaleDateString('pt-BR');
                          })()} • 
                          Qtd: {mov.quantidade}
                          {mov.observacao && !isMobile && ` • ${mov.observacao.substring(0, 30)}...`}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ 
                textAlign: 'center', 
                padding: '20px', 
                color: '#999',
                fontSize: isMobile ? '14px' : '16px'
              }}>
                Nenhuma movimentação recente
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EstoqueDashboard;
